import { cognitoConfig, impersonationCognitoConfig } from "@src/appV2/Auth/cognito/config";
import { getMagicLinkRedirectUrl } from "@src/appV2/Auth/LoginSignUp/libs/utils/getMagicLinkRedirectUrl";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logError, logEvent } from "@src/appV2/lib/analytics/log";
import { Amplify } from "aws-amplify";
import {
  AuthSession,
  autoSignIn,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  signIn,
  signOut,
  signUp,
  updateUserAttributes,
} from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { Hub } from "aws-amplify/utils";
import { v4 as uuidv4 } from "uuid";

import { getCognitoLocalStorageState } from "./cognitoLocalStorageInspector";
import { clearMagicLinkEmail, setMagicLinkEmail } from "./magicLinkEmailStore";
import {
  clearPendingSignUpUsername,
  getPendingSignUpUsername,
  setPendingSignUpUsername,
} from "./pendingSignUpDataStore";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: cognitoConfig.userPoolId,
      userPoolClientId: cognitoConfig.clientId,
    },
  },
});

// Configure Amplify to use localStorage for token persistence. This is
// necessary to guarantee the signup with auto sign-in works.
cognitoUserPoolsTokenProvider.setKeyValueStorage({
  async getItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  },
  async setItem(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  },
  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  },
  async clear(): Promise<void> {
    localStorage.clear();
  },
});

export interface CognitoUser {
  uid: string;
  email: string;
  emailVerified: boolean;
  phoneNumber?: string;
  displayName?: string;
  cbhUserId?: string;
}

export interface CognitoUserCredential {
  user: CognitoUser;
  idToken: string;
  refreshToken: string;
  accessToken?: string;
}

interface SignInWithFirebaseTokenParams {
  userIdentifier: string;
  firebaseIdToken: string;
}

interface Unsubscribe {
  (): void;
}

const CHALLENGE_TYPE = {
  MAGIC_LINK: "MAGIC_LINK",
  SMS_OTP: "SMS_OTP",
  FIREBASE_TOKEN_EXCHANGE: "FIREBASE_TOKEN_EXCHANGE",
  IMPERSONATION: "IMPERSONATION",
} as const;

type ChallengeType = (typeof CHALLENGE_TYPE)[keyof typeof CHALLENGE_TYPE];

function stringifyUnknownError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object") {
    // Handle objects with a message property but missing name/__type
    if ("message" in error && typeof (error as { message: unknown }).message === "string") {
      return (error as { message: string }).message;
    }

    try {
      return JSON.stringify(error);
    } catch {
      // Circular references or other stringify failures
    }
  }

  return String(error);
}

export class CognitoAuthentication {
  private tokenChangeListeners: Array<(user: CognitoUser | undefined) => Promise<void>> = [];
  private errorListeners: Array<(error: any) => void> = [];
  private challengeSession: string | null = null;

  constructor() {
    Hub.listen("auth", (data) => {
      switch (data.payload.event) {
        case "tokenRefresh":
          void this.handleHubTokenRefresh();
          break;
        case "tokenRefresh_failure": {
          const amplifyError = (data.payload.data as { error?: Error })?.error;
          logError(APP_V2_APP_EVENTS.COGNITO_TOKEN_REFRESH_FAILURE_DETAIL, {
            error: amplifyError ?? new Error("Cognito token refresh failed"),
            metadata: {
              errorName: amplifyError?.name,
              errorMessage: amplifyError?.message,
              ...getCognitoLocalStorageState(),
            },
          });
          this.emitError(amplifyError ?? new Error("Cognito token refresh failed"));
          break;
        }
        default:
          break;
      }
    });
  }

  get hasActiveSession(): boolean {
    return this.challengeSession !== null;
  }

  private async handleHubTokenRefresh(): Promise<void> {
    try {
      const user = await this.getCurrentUserSession();
      await this.emitTokenChange(user);
    } catch (error) {
      this.emitError(error);
    }
  }

  // Initiate challenge selection (first step in custom auth flow)
  async initiateCustomAuth(username: string): Promise<{ challengeName: string; session?: string }> {
    try {
      await this.signOutIfAuthenticated();

      const signInResult = await signIn({
        username,
        options: {
          authFlowType: "CUSTOM_WITHOUT_SRP",
          clientMetadata: {
            "client_metadata:custom_challenge:select": "init",
          },
        },
      });

      // Store session info for challenge response - Amplify handles this internally
      this.challengeSession = "amplify-managed-session";

      return {
        challengeName: signInResult.nextStep?.signInStep || "CUSTOM_CHALLENGE",
        session: this.challengeSession || undefined,
      };
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  private async signOutIfAuthenticated(): Promise<void> {
    const session = await fetchAuthSession();
    if (session.tokens) {
      logEvent(APP_V2_APP_EVENTS.COGNITO_SIGN_OUT_BEFORE_RETRY);
      await signOut();
      this.challengeSession = null;
    }
  }

  // Select challenge type (second step in custom auth flow)
  async selectChallengeType(
    challengeType: ChallengeType,
    options?: { skipChallengeCreation?: boolean }
  ): Promise<{ session: string; challengeParameters: Record<string, string> }> {
    try {
      const clientMetadata: Record<string, string> = {
        target_custom_challenge: challengeType,
        sub_challenge_step: "select",
      };

      // Add requester URL for magic link flow
      if (challengeType === CHALLENGE_TYPE.MAGIC_LINK) {
        clientMetadata.requester_url = getMagicLinkRedirectUrl();
      }

      if (options?.skipChallengeCreation) {
        clientMetadata.skip_challenge_creation = "true";
      }

      const result = await confirmSignIn({
        challengeResponse: challengeType,
        options: {
          clientMetadata,
        },
      });

      // Check if the challenge response contains an error status
      // Challenge parameters can be in different locations depending on Amplify version
      const nextStepData = result.nextStep as any;
      const challengeParams =
        nextStepData?.additionalInfo || nextStepData?.challengeParameters || {};

      if (challengeParams.status && challengeParams.status !== "success") {
        const error = new Error(challengeParams.message || "Challenge failed with error status");
        error.name = "ChallengeError";
        throw error;
      }

      return {
        session: "amplify-managed-session",
        challengeParameters: challengeParams as Record<string, string>,
      };
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  // Respond to custom challenge (final step - token/OTP confirmation)
  async respondToCustomChallenge(
    challengeAnswer: string,
    challengeType: ChallengeType,
    additionalMetadata?: Record<string, string>
  ): Promise<CognitoUserCredential> {
    try {
      const confirmResult = await confirmSignIn({
        challengeResponse: challengeAnswer,
        options: {
          clientMetadata: {
            target_custom_challenge: challengeType,
            sub_challenge_step: "answer",
            [`client_metadata:custom_challenge:${challengeType.toLowerCase()}`]: "verify",
            ...additionalMetadata,
          },
        },
      });

      // Check if the challenge response contains an error status
      // Challenge parameters can be in different locations depending on Amplify version
      const nextStepData = confirmResult.nextStep as any;
      const challengeParams =
        nextStepData?.additionalInfo || nextStepData?.challengeParameters || {};

      if (challengeParams.status && challengeParams.status !== "success") {
        const error = new Error(challengeParams.message || "Challenge failed with error status");
        error.name = "ChallengeError";
        throw error;
      }

      if (confirmResult.isSignedIn) {
        const session = await fetchAuthSession();
        const user = this.extractUserFromSession(session);

        const credentials: CognitoUserCredential = {
          user,
          idToken: session.tokens?.idToken?.toString() || "",
          refreshToken: "", // Amplify handles refresh tokens internally
          accessToken: session.tokens?.accessToken?.toString(),
        };

        await this.emitTokenChange(user);
        return credentials;
      }

      throw new Error("Authentication not completed");
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  // Firebase-compatible methods (wrappers around custom auth flow)
  // Initiate magic link request (triggers email sending)
  async requestMagicLink(email: string): Promise<{ status: string; message: string }> {
    try {
      // Step 1: Initiate challenge selection
      const { session: initialSession } = await this.initiateCustomAuth(email);
      if (!initialSession) {
        throw new Error("Failed to get initial session");
      }

      // Step 2: Select MAGIC_LINK challenge type (this triggers email sending)
      await this.selectChallengeType(CHALLENGE_TYPE.MAGIC_LINK);

      // Store email so a fresh page can re-initiate the auth flow
      setMagicLinkEmail(email);

      logEvent(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_REQUESTED, {
        hasEmail: Boolean(email),
      });

      return {
        status: "success",
        message: "Magic link sent to your email",
      };
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  // Phone OTP flow using USER_AUTH (not custom challenge)
  async requestPhoneOtp(phoneNumber: string): Promise<{ status: string; message: string }> {
    try {
      await this.signOutIfAuthenticated();

      const signInResult = await signIn({
        username: phoneNumber,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: CHALLENGE_TYPE.SMS_OTP,
        },
      });

      const signInStep = signInResult.nextStep?.signInStep;
      if (signInStep !== "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        logError(APP_V2_APP_EVENTS.COGNITO_OTP_REQUESTED, {
          error: "SMS OTP challenge not available",
          metadata: { signInStep, hasPhoneNumber: Boolean(phoneNumber) },
        });
        const error = new Error("SMS OTP challenge not available for this user");
        Object.assign(error, { code: "auth/sms-otp-not-available" });
        throw error;
      }

      logEvent(APP_V2_APP_EVENTS.COGNITO_OTP_REQUESTED, {
        hasPhoneNumber: Boolean(phoneNumber),
      });

      return {
        status: "success",
        message: "OTP sent to your phone",
      };
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async confirmPhoneOtp(otpCode: string): Promise<CognitoUserCredential> {
    try {
      const confirmResult = await confirmSignIn({
        challengeResponse: otpCode,
      });

      if (confirmResult.isSignedIn) {
        const session = await fetchAuthSession();
        const user = this.extractUserFromSession(session);

        const credentials: CognitoUserCredential = {
          user,
          idToken: session.tokens?.idToken?.toString() || "",
          refreshToken: "", // Amplify handles refresh tokens internally
          accessToken: session.tokens?.accessToken?.toString(),
        };

        logEvent(APP_V2_APP_EVENTS.COGNITO_OTP_CONFIRMED, {
          userId: user.uid,
        });

        await this.emitTokenChange(user);
        return credentials;
      }

      throw new Error("Authentication not completed");
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async signInWithEmailLink(link: string, email?: string): Promise<CognitoUserCredential> {
    try {
      // Parse the magic link to extract the payload
      const url = new URL(link);
      const payload = url.searchParams.get("payload");
      if (!payload) {
        throw new Error("No payload found in magic link");
      }

      // On a fresh page the in-memory session is lost. Re-initiate the full
      // custom auth flow so Amplify has an active session before we answer.
      if (!this.hasActiveSession && email) {
        logEvent(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_FRESH_PAGE_AUTH, {
          hasEmail: true,
        });

        await this.initiateCustomAuth(email);
        // Advance the state machine through the select step without re-sending
        // the email. The original magic link token is still valid in Redis.
        await this.selectChallengeType(CHALLENGE_TYPE.MAGIC_LINK, {
          skipChallengeCreation: true,
        });
      }

      if (!this.hasActiveSession) {
        const error = new Error("No active session. Please enter your email to continue.");
        error.name = "NoActiveSessionError";
        throw error;
      }

      const credentials = await this.respondToCustomChallenge(payload, CHALLENGE_TYPE.MAGIC_LINK);

      clearMagicLinkEmail();

      logEvent(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_CONFIRMED, {
        userId: credentials.user.uid,
      });

      return credentials;
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async signInWithFirebaseToken(
    params: SignInWithFirebaseTokenParams
  ): Promise<CognitoUserCredential> {
    const { userIdentifier, firebaseIdToken } = params;

    try {
      await this.initiateCustomAuth(userIdentifier);
      await this.selectChallengeType(CHALLENGE_TYPE.FIREBASE_TOKEN_EXCHANGE);
      return await this.respondToCustomChallenge(
        firebaseIdToken,
        CHALLENGE_TYPE.FIREBASE_TOKEN_EXCHANGE
      );
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async getCurrentUserInfo(): Promise<CognitoUser | undefined> {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens) {
        return undefined;
      }
      return this.extractUserFromSession(session);
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return undefined;
    }
  }

  get currentUser(): CognitoUser | undefined {
    // This will be handled by onIdTokenChanged listener
    return undefined;
  }

  async updateCurrentUserName(name: string): Promise<void> {
    try {
      await updateUserAttributes({
        userAttributes: {
          name,
        },
      });

      // Trigger token change to update cached user
      const session = await fetchAuthSession();
      const user = this.extractUserFromSession(session);
      await this.emitTokenChange(user);
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async updateCurrentUserEmail(email: string): Promise<void> {
    try {
      await updateUserAttributes({
        userAttributes: {
          email,
        },
      });

      // Trigger token change to update cached user
      const session = await fetchAuthSession();
      const user = this.extractUserFromSession(session);
      await this.emitTokenChange(user);
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  onIdTokenChanged(
    callback: (user: CognitoUser | undefined) => Promise<void>,
    errorCallback: (error: any) => void
  ): Unsubscribe {
    this.tokenChangeListeners.push(callback);
    this.errorListeners.push(errorCallback);

    // Initial call with current user
    this.getCurrentUserSession()
      .then((user) => callback(user))
      .catch(errorCallback);

    return () => {
      const callbackIndex = this.tokenChangeListeners.indexOf(callback);
      if (callbackIndex > -1) {
        this.tokenChangeListeners.splice(callbackIndex, 1);
      }

      const errorIndex = this.errorListeners.indexOf(errorCallback);
      if (errorIndex > -1) {
        this.errorListeners.splice(errorIndex, 1);
      }
    };
  }

  async signInAsImpersonator(
    cognitoUsername: string,
    employeeIdToken: string
  ): Promise<CognitoUserCredential> {
    // Reconfigure Amplify with the impersonation client ID. This is permanent
    // for the session — once impersonation starts, Amplify stays on this client.
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: impersonationCognitoConfig.userPoolId,
          userPoolClientId: impersonationCognitoConfig.clientId,
        },
      },
    });

    cognitoUserPoolsTokenProvider.setKeyValueStorage({
      async getItem(key: string): Promise<string | null> {
        return localStorage.getItem(key);
      },
      async setItem(key: string, value: string): Promise<void> {
        localStorage.setItem(key, value);
      },
      async removeItem(key: string): Promise<void> {
        localStorage.removeItem(key);
      },
      async clear(): Promise<void> {
        localStorage.clear();
      },
    });

    await this.initiateCustomAuth(cognitoUsername);
    const { challengeParameters } = await this.selectChallengeType(CHALLENGE_TYPE.IMPERSONATION);

    return this.respondToCustomChallenge(employeeIdToken, CHALLENGE_TYPE.IMPERSONATION, {
      impersonation_nonce: challengeParameters.nonce,
    });
  }

  async signOut(): Promise<void> {
    try {
      await signOut();
      this.challengeSession = null;
      await this.emitTokenChange(undefined);
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async getIdToken(forceRefresh?: boolean): Promise<string | undefined> {
    const session = await fetchAuthSession({ forceRefresh });
    return session.tokens?.idToken?.toString();
  }

  async signUp(phoneNumberInE164: string): Promise<string> {
    logEvent(APP_V2_APP_EVENTS.COGNITO_SIGNUP_INITIATED, {
      hasPhoneNumber: Boolean(phoneNumberInE164),
    });

    try {
      const username = uuidv4();
      await signUp({
        username,
        options: {
          userAttributes: {
            phone_number: phoneNumberInE164,
          },
          autoSignIn: {
            authFlowType: "USER_AUTH",
          },
        },
      });
      setPendingSignUpUsername(username);
      return username;
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      logError(APP_V2_APP_EVENTS.COGNITO_SIGNUP_FAILED, {
        error,
        metadata: { errorCode: mappedError.code, errorMessage: mappedError.message },
      });
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async signUpWithEmail(email: string): Promise<string> {
    logEvent(APP_V2_APP_EVENTS.COGNITO_EMAIL_SIGNUP_INITIATED, {
      hasEmail: Boolean(email),
    });

    try {
      const username = uuidv4();
      // The Cognito user pool requires each user to have at least one allowed
      // first auth factor (SMS_OTP or PASSWORD). Since we only pass email (no
      // phone_number), we must supply a password to satisfy the PASSWORD factor.
      // This password is never used — authentication goes through CUSTOM_WITHOUT_SRP
      // (magic link). We generate a random value that meets the pool's policy
      // (≥12 chars, uppercase + lowercase).
      const password = `${uuidv4()}${uuidv4()}`.replace(/-/g, "Ax");
      await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      return username;
    } catch (error) {
      // UsernameExistsException is expected when the user already exists in Cognito.
      // Skip error logging/emitting for this case to avoid false error signals in monitoring,
      // since callers use signUpWithEmail as a pre-check before signIn.
      if (this.isUsernameExistsError(error)) {
        throw error;
      }

      const mappedError = this.mapAmplifyError(error);
      logError(APP_V2_APP_EVENTS.COGNITO_EMAIL_SIGNUP_FAILED, {
        error,
        metadata: { errorCode: mappedError.code, errorMessage: mappedError.message },
      });
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  private isUsernameExistsError(error: unknown): boolean {
    if (!error || typeof error !== "object") {
      return false;
    }

    const errorType =
      ("name" in error ? (error as { name?: unknown }).name : undefined) ||
      ("__type" in error ? (error as { __type?: unknown }).__type : undefined);

    if (typeof errorType !== "string") {
      return false;
    }

    return (
      errorType === "UsernameExistsException" || errorType.endsWith("#UsernameExistsException")
    );
  }

  async confirmSignUp(confirmationCode: string): Promise<CognitoUserCredential> {
    try {
      const username = getPendingSignUpUsername();
      if (!username) {
        throw new Error("No pending sign up found. Call signUp first.");
      }

      await confirmSignUp({
        username,
        confirmationCode,
        options: {
          forceAliasCreation: true,
        },
      });

      clearPendingSignUpUsername();

      const autoSignInResult = await autoSignIn();

      if (autoSignInResult.isSignedIn) {
        const session = await fetchAuthSession();
        const user = this.extractUserFromSession(session);

        const credentials: CognitoUserCredential = {
          user,
          idToken: session.tokens?.idToken?.toString() || "",
          refreshToken: "", // Amplify handles refresh tokens internally
          accessToken: session.tokens?.accessToken?.toString(),
        };

        logEvent(APP_V2_APP_EVENTS.COGNITO_SIGNUP_CONFIRMED, {
          userId: user.uid,
        });

        await this.emitTokenChange(user);
        return credentials;
      }

      throw new Error("Auto sign-in failed after signup confirmation");
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      logError(APP_V2_APP_EVENTS.COGNITO_SIGNUP_FAILED, {
        error,
        metadata: { errorCode: mappedError.code, errorMessage: mappedError.message },
      });
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  async updateUserAttributes(attributes: Record<string, string>): Promise<void> {
    try {
      await updateUserAttributes({
        userAttributes: attributes,
      });

      const session = await fetchAuthSession();
      const user = this.extractUserFromSession(session);
      await this.emitTokenChange(user);
    } catch (error) {
      const mappedError = this.mapAmplifyError(error);
      this.emitError(mappedError);
      throw mappedError;
    }
  }

  private async getCurrentUserSession(): Promise<CognitoUser | undefined> {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens) {
        return undefined;
      }
      return this.extractUserFromSession(session);
      // FIXME: Automatically disabled during ESLint config update
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return undefined;
    }
  }

  private extractUserFromSession(session: AuthSession): CognitoUser {
    const idToken = session.tokens?.idToken;
    if (!idToken) {
      throw new Error("No ID token available");
    }

    // Parse JWT payload manually since we need the claims
    const payload = this.parseJwtPayload(idToken.toString());

    return {
      uid: payload.sub,
      email: payload.email || "",
      emailVerified: payload.email_verified || false,
      phoneNumber: payload.phone_number,
      displayName: payload.name,
      cbhUserId: payload["custom:cbh_user_id"],
    };
  }

  private parseJwtPayload(token: string): any {
    const [, payload] = token.split(".");
    if (token.split(".").length !== 3) {
      throw new Error("Invalid JWT format");
    }
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  }

  private async emitTokenChange(user: CognitoUser | undefined): Promise<void> {
    const promises = this.tokenChangeListeners.map((listener) =>
      listener(user).catch((error) => {
        this.emitError(error);
      })
    );
    await Promise.all(promises);
  }

  private emitError(error: any): void {
    this.errorListeners.forEach((listener) => {
      try {
        listener(error);
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (listenerError) {
        // Ignore listener errors to prevent cascading failures
      }
    });
  }

  private mapAmplifyError(error: unknown): { code: string; message: string } {
    // If error is already a mapped error object, return it as-is
    if (error && typeof error === "object" && "code" in error && "message" in error) {
      return error as { code: string; message: string };
    }

    if (error && typeof error === "object" && ("name" in error || "__type" in error)) {
      const amplifyError = error as { name?: string; __type?: string; message?: string };
      const errorType = amplifyError.name || amplifyError.__type || "";

      switch (errorType) {
        case "UserNotConfirmedException":
          return {
            code: "auth/email-not-verified",
            message: "Email address not verified",
          };
        case "NotAuthorizedException":
          return {
            code: "auth/wrong-password",
            message: "Invalid credentials",
          };
        case "UserNotFoundException":
          return {
            code: "auth/user-not-found",
            message: "User not found",
          };
        case "InvalidParameterException":
          return {
            code: "auth/invalid-email",
            message: "Invalid email format",
          };
        case "TooManyRequestsException":
          return {
            code: "auth/too-many-requests",
            message: "Too many requests, please try again later",
          };
        case "LimitExceededException":
          return {
            code: "auth/limit-exceeded",
            message: "Attempt limit exceeded, please try after some time.",
          };
        case "NetworkError":
          return {
            code: "auth/network-request-failed",
            message: "Network error occurred",
          };
        case "ChallengeError":
          return {
            code: "auth/challenge-error",
            message: amplifyError.message || "Challenge failed",
          };
        default:
          return {
            code: "auth/unknown",
            message: amplifyError.message || "An unknown error occurred",
          };
      }
    }

    return {
      code: "auth/unknown",
      message: stringifyUnknownError(error),
    };
  }
}
